import * as React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Check, Search, X } from "lucide-react";

/**
 * PhoneInput - Enterprise-grade phone input with smart country detection
 * 
 * UX Philosophy:
 * - Default → Lock → Optional Change pattern
 * - 80-90% of users never need to change country
 * - Search-first country picker (modal, not dropdown)
 * - Calm, deliberate, executive-friendly
 */

// Country data with dial codes
const countryCodes = [
  { code: "+1", country: "US", name: "United States" },
  { code: "+1", country: "CA", name: "Canada" },
  { code: "+44", country: "GB", name: "United Kingdom" },
  { code: "+91", country: "IN", name: "India" },
  { code: "+49", country: "DE", name: "Germany" },
  { code: "+33", country: "FR", name: "France" },
  { code: "+39", country: "IT", name: "Italy" },
  { code: "+34", country: "ES", name: "Spain" },
  { code: "+61", country: "AU", name: "Australia" },
  { code: "+86", country: "CN", name: "China" },
  { code: "+81", country: "JP", name: "Japan" },
  { code: "+82", country: "KR", name: "South Korea" },
  { code: "+55", country: "BR", name: "Brazil" },
  { code: "+52", country: "MX", name: "Mexico" },
  { code: "+971", country: "AE", name: "UAE" },
  { code: "+65", country: "SG", name: "Singapore" },
  { code: "+41", country: "CH", name: "Switzerland" },
  { code: "+31", country: "NL", name: "Netherlands" },
  { code: "+46", country: "SE", name: "Sweden" },
  { code: "+47", country: "NO", name: "Norway" },
  { code: "+45", country: "DK", name: "Denmark" },
  { code: "+358", country: "FI", name: "Finland" },
  { code: "+48", country: "PL", name: "Poland" },
  { code: "+43", country: "AT", name: "Austria" },
  { code: "+32", country: "BE", name: "Belgium" },
  { code: "+353", country: "IE", name: "Ireland" },
  { code: "+351", country: "PT", name: "Portugal" },
  { code: "+27", country: "ZA", name: "South Africa" },
  { code: "+234", country: "NG", name: "Nigeria" },
  { code: "+254", country: "KE", name: "Kenya" },
  { code: "+20", country: "EG", name: "Egypt" },
  { code: "+972", country: "IL", name: "Israel" },
  { code: "+90", country: "TR", name: "Turkey" },
  { code: "+7", country: "RU", name: "Russia" },
  { code: "+380", country: "UA", name: "Ukraine" },
  { code: "+66", country: "TH", name: "Thailand" },
  { code: "+84", country: "VN", name: "Vietnam" },
  { code: "+60", country: "MY", name: "Malaysia" },
  { code: "+62", country: "ID", name: "Indonesia" },
  { code: "+63", country: "PH", name: "Philippines" },
  { code: "+64", country: "NZ", name: "New Zealand" },
  { code: "+54", country: "AR", name: "Argentina" },
  { code: "+56", country: "CL", name: "Chile" },
  { code: "+57", country: "CO", name: "Colombia" },
  { code: "+51", country: "PE", name: "Peru" },
];

// Suggested/common countries (pinned at top)
const suggestedCountryCodes = ["US", "GB", "IN", "DE", "AU", "CA"];

// Map country codes to their data
const countryCodeMap: Record<string, typeof countryCodes[0]> = {};
countryCodes.forEach((c) => {
  if (!countryCodeMap[c.country]) {
    countryCodeMap[c.country] = c;
  }
});

// Get country by dial code
const getCountryByDialCode = (dialCode: string) => {
  return countryCodes.find(c => c.code === dialCode);
};

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  countryCode: string;
  onCountryCodeChange: (code: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export function PhoneInput({
  value,
  onChange,
  countryCode,
  onCountryCodeChange,
  placeholder = "Enter your phone number",
  className,
  disabled = false,
}: PhoneInputProps) {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const currentCountry = getCountryByDialCode(countryCode);

  // Filter countries based on search
  const filteredCountries = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return countryCodes;
    }
    const query = searchQuery.toLowerCase();
    return countryCodes.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.code.includes(query) ||
        c.country.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Get suggested countries
  const suggestedCountries = React.useMemo(() => {
    return suggestedCountryCodes
      .map(code => countryCodeMap[code])
      .filter(Boolean);
  }, []);

  // Handle country selection
  const handleSelectCountry = (country: typeof countryCodes[0]) => {
    onCountryCodeChange(country.code);
    setIsModalOpen(false);
    setSearchQuery("");
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsModalOpen(false);
      setSearchQuery("");
    }
  };

  // Focus search input when modal opens
  React.useEffect(() => {
    if (isModalOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isModalOpen]);

  return (
    <div className={cn("space-y-1.5", className)}>
      {/* Main input row */}
      <div className="flex">
        {/* Country code display (locked by default) */}
        <div className="flex items-center px-3 bg-muted/50 border border-r-0 border-input rounded-l-md min-w-[70px]">
          <span className="text-sm font-medium text-foreground">
            {countryCode}
          </span>
        </div>
        
        {/* Phone number input */}
        <Input
          type="tel"
          value={value}
          onChange={(e) => {
            // Only allow digits, spaces, and dashes
            const cleaned = e.target.value.replace(/[^\d\s-]/g, '');
            onChange(cleaned);
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 rounded-l-none border-l-0"
        />
      </div>

      {/* Detected country + Change link */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>
          Detected: {currentCountry?.name || "Unknown"}
        </span>
        <span className="text-muted-foreground/50">·</span>
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="text-primary hover:text-primary/80 transition-colors font-medium"
          disabled={disabled}
        >
          Change
        </button>
      </div>

      {/* Country Picker Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent 
          className="sm:max-w-md p-0 gap-0"
          onKeyDown={handleKeyDown}
        >
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="text-lg font-medium">
              Select country
            </DialogTitle>
          </DialogHeader>

          {/* Search input */}
          <div className="px-4 py-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search country or dial code..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Country list */}
          <div className="max-h-[320px] overflow-y-auto">
            {/* Suggested countries (only when not searching) */}
            {!searchQuery && (
              <div className="p-2 border-b border-border">
                <span className="px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Suggested
                </span>
                <div className="mt-1">
                  {suggestedCountries.map((country) => (
                    <CountryOption
                      key={`suggested-${country.country}`}
                      country={country}
                      isSelected={country.code === countryCode}
                      onClick={() => handleSelectCountry(country)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All countries / Search results */}
            <div className="p-2">
              {!searchQuery && (
                <span className="px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  All countries
                </span>
              )}
              <div className={searchQuery ? "" : "mt-1"}>
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country, index) => (
                    <CountryOption
                      key={`${country.country}-${index}`}
                      country={country}
                      isSelected={country.code === countryCode && country.country === currentCountry?.country}
                      onClick={() => handleSelectCountry(country)}
                    />
                  ))
                ) : (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Country option component
interface CountryOptionProps {
  country: typeof countryCodes[0];
  isSelected: boolean;
  onClick: () => void;
}

function CountryOption({ country, isSelected, onClick }: CountryOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center justify-between px-2 py-2.5 rounded-md text-left transition-colors",
        "hover:bg-muted/50 focus:bg-muted/50 focus:outline-none",
        isSelected && "bg-primary/5"
      )}
    >
      <div className="flex items-center gap-3">
        {/* Dial code as primary signal */}
        <span className="text-sm font-medium text-foreground min-w-[50px]">
          {country.code}
        </span>
        {/* Country name as secondary */}
        <span className="text-sm text-muted-foreground">
          {country.name}
        </span>
      </div>
      {isSelected && (
        <Check className="h-4 w-4 text-primary flex-shrink-0" />
      )}
    </button>
  );
}

// Hook to detect country from IP
export function useCountryFromIP(defaultCode: string = "+1") {
  const [countryCode, setCountryCode] = React.useState(defaultCode);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const detectCountry = async () => {
      try {
        // Using ipapi.co free tier for IP geolocation
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000), // 3 second timeout
        });
        
        if (response.ok) {
          const data = await response.json();
          const countryData = countryCodeMap[data.country_code];
          if (countryData) {
            setCountryCode(countryData.code);
          }
        }
      } catch (error) {
        // Silently fail and use default
        console.log('Could not detect country from IP, using default');
      } finally {
        setIsLoading(false);
      }
    };

    detectCountry();
  }, []);

  return { countryCode, setCountryCode, isLoading };
}

export { countryCodes };